import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Empty, ColumnDefinition, Button } from 'barte-design-system';
import { usePostHog } from '@posthog/react';

import { TableSkeleton } from '@/modules/shared/components'
import { useGetRatePlansSellersList } from '@payfac/services';
import { ROUTES_LEGACY_WEBAPP, TRACK_EVENTS } from '@payfac/constants';

import styles from './rate-plan-sellers.module.css';

import type { IRatePlanSellerList } from '@payfac/types';
import type { RatePlanSellersProps } from './rate-plan-sellers.type';


export const RatePlanSellers = ({ planId }: RatePlanSellersProps) => {
    const { t } = useTranslation();
    const posthog = usePostHog();
    const [page, setPage] = useState(1);

    const { data: response, isLoading } = useGetRatePlansSellersList({
        planId: Number(planId),
        filters: { page, limit: 10 }
    });

    const sellers = response?.data?.data || [];

    const showEmptyState = !isLoading && (sellers?.length === 0 || response?.data?.pagination?.total === 0);

    const onClickEditSeller = (sellerId: string) => {
        posthog.capture(TRACK_EVENTS.RATE_PLAN.SELLER_EDIT_CLICKED, { seller_id: sellerId });
        window.location.href = ROUTES_LEGACY_WEBAPP.SELLERS.EDIT_TAXES.fullPath(sellerId);
    };

    const columns: ColumnDefinition<IRatePlanSellerList>[] = [
        {
            key: 'id',
            title: t('payfac.pageRatePlanSellersTableColumnId'),
            dataIndex: 'id'
        },
        {
            key: 'companyName',
            title: t('payfac.pageRatePlanSellersTableColumnName'),
            dataIndex: 'companyName'
        },
        {
            key: 'document',
            title: t('payfac.pageRatePlanSellersTableColumnDocument'),
            dataIndex: 'document'
        },
        {
            key: 'actions',
            title: '',
            dataIndex: 'id',
            render: (id) => (
                <div className={styles['edit-button-container']}>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onClickEditSeller(String(id))}
                    >
                        {t('payfac.pageRatePlanSellersEditLinkButton')}
                    </Button>
                </div>
            )
        }
    ];

    return (
        <div className={styles.container}>
            {showEmptyState && <Empty showButton={false} />}

            {!showEmptyState && (
                <div className={styles.table}>
                    <TableSkeleton<IRatePlanSellerList>
                        isLoading={isLoading}
                        columns={columns}
                        data={sellers}
                        rowKey={record => record.id}
                        pagination={{
                            currentPage: page,
                            totalPages: response?.data?.pagination?.pages || 1,
                            onPageChange: setPage
                        }}
                    />
                </div>
            )}
        </div>
    );
};
